/*******************************************************************
* PAGE        : PromoCode
* REMARKS     :
* CREATED ON  : 27-08-2026
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 27-08-2026
********************************************************************/

import { TablePagination, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import addsquare from "../../assets/add-square.svg";
import deleteicon from "../../assets/delete.svg";
import filtericon from "../../assets/filter-icon.svg";
import ticketicon from "../../assets/ticket-icon.svg";
import DialogComponent from '../../components/DialogComponent/DialogComponent';
import LoaderComponent from '../../components/LoaderComponent/LoaderComponent';
import NotificationComponent from '../../components/NotificationComponent/NotificationComponent';
import { DeletePromoCode, GetPromoCodes } from '../../services/apiController';
import AddUpdatePromocode from './AddUpdatePromocode';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';

// #region Component
const PromoCode = () => {

    // #region States
    const navigate = useNavigate();

    const { state } = useLocation();

    const eventMasterId = state?.eventMasterId;

    const [loading, setLoading] = useState(false);

    const [openPopup, setOpenPopup] = useState(false);
    const [openPopupAction, setOpenPopupAction] = useState(0);
    const [selectedPromoData, setSelectedPromoData] = useState({});

    const [promoList, setPromoList] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Default rows per page
    const [currentPage, setCurrentPage] = useState(1); // Default current page
    const [maxRows, setMaxRows] = useState(0); // Total number of rows
    const [gotoPage, setGotoPage] = useState(1); // Page input for jump
    const [totalPages, setTotalPages] = useState(1); // Total pages calculation
    const [searchQuery, setSearchQuery] = useState(''); // For search query
    const [sortOrder, setSortOrder] = useState({ column: '', order: 'asc' }); // For sorting state

    const [columnCount, setColumnCount] = useState(1);

    // State to manage snackbar visibility and messages
    const [snackbarStates, setSnackbarStates] = useState({ isSnackbarOpen: false, snackbarMessage: '' });
    const handleCloseSnackbar = () => {
        setSnackbarStates({ isSnackbarOpen: false, snackbarMessage: '', snackbarSeverity: '' });
    };
    // #endregion

    useEffect(() => {
        const thCount = document.querySelectorAll('thead th').length;
        setColumnCount(thCount || 1);
    }, []);

    useEffect(() => {
        wsGetPromoCodes();
    }, [rowsPerPage, currentPage]); // Re-run the effect when rowsPerPage or currentPage changes

    // #region API Methods
    const wsGetPromoCodes = async () => {
        setLoading(true);
        GetPromoCodes({ ticketMasterId: eventMasterId })
            .then((response) => {
                if (response) {
                    setPromoList(response?.promoCodes);
                    setMaxRows(response?.promoCodes?.length); // Set total rows from response
                    setTotalPages(Math.ceil(response?.promoCodes?.length / rowsPerPage)); // Calculate total pages
                } else {
                    // Handle error scenario
                }
                setLoading(false);
            }).catch((error) => {
                setLoading(false);
                // Handle API error
            });
    }

    const wsDeletePromoCode = async (ticketPromoCodeId) => {
        // DeletePromoCode
        setLoading(true);
        const params = {
            ticketMasterId: eventMasterId,
            TicketPromoCodeId: ticketPromoCodeId
        }

        DeletePromoCode(params).then((response) => {
            if (response.status) {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response?.message || "Promocode delete successfully", snackbarSeverity: "success" });
            } else {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response.message, snackbarSeverity: 'error' });
            }
            handleClose();
            setLoading(false);
        }).catch((error) => {
            setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: "Something went wrong please try again", snackbarSeverity: 'error' });
            handleClose();
            setLoading(false);
        });
    }
    //#endregion

    // #region Methods
    // Handle page change
    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage + 1); // MUI TablePagination uses 0-based index
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(1); // Reset to page 1 when rows per page changes
    };

    // Handle page input change
    const handleGotoPageChange = (event) => {
        setGotoPage(event.target.value);
    };

    // Handle the submit (Enter key press) for "Go to page"
    const handleSubmitGotoPage = (event) => {
        if (event.key === 'Enter') {
            const pageNumber = Math.max(1, Math.min(totalPages, parseInt(gotoPage, 10))); // Ensure valid page
            setCurrentPage(pageNumber);
        }
    };

    // Handle search query change
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Filtered and sorted promoList
    const SEARCH_FIELDS = [
        'PromoCode',
        'MaxTicket',
        'IsDefault',
        'IsActive'
    ];

    const filteredPromos = promoList?.filter((promo) =>
        SEARCH_FIELDS.some((field) =>
            promo[field]
                ?.toString()
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
        )
    );

    // Sorting function
    const handleSort = (column) => {
        const newOrder = sortOrder.order === 'asc' ? 'desc' : 'asc';
        setSortOrder({
            column: column,
            order: newOrder,
        });
    };

    const sortedPromos = filteredPromos.sort((a, b) => {
        if (!sortOrder.column) return 0;

        const aValue = a[sortOrder.column];
        const bValue = b[sortOrder.column];

        if (aValue < bValue) return sortOrder.order === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder.order === 'asc' ? 1 : -1;
        return 0;
    });

    const handleDeletePromoCode = () => {
        wsDeletePromoCode(selectedPromoData?.TicketPromoCodeId || 0)
    }

    const handleClose = () => {
        setOpenPopup(false);
        setOpenPopupAction(0);
        wsGetPromoCodes();
    }

    // #endregion

    return (
        loading ? <LoaderComponent />
            : <React.Fragment>
                <NotificationComponent isOpen={snackbarStates.isSnackbarOpen} message={snackbarStates.snackbarMessage} onClose={handleCloseSnackbar} severity={snackbarStates.snackbarSeverity} />

                <div style={{ padding: '10px 20px 50px', overflowX: 'auto' }}>
                    <div className='table-heading-search' style={{ marginBottom: '10px', }}>
                        <h2 className='dialog-heading' style={{ marginBlockEnd: '0px' }}>Promocode</h2>

                        {/* Search Input */}
                        <div className='table-heading-search' style={{ alignItems: 'center', gap: '16px' }}>
                            Search: <input type="text" value={searchQuery} onChange={handleSearchChange} style={{ padding: '5px', width: '200px', border: '1px solid #ccc', borderRadius: '4px', }} />
                            <ButtonComponent style={{ padding: "8px 16px", textTransform: 'none' }} onClick={() => { setOpenPopup(true); setOpenPopupAction(1); setSelectedPromoData({}); }}>Add New Promocode</ButtonComponent>

                            {/* <img src={addsquare} className="dialog-close-icon" onClick={() => { setOpenPopup(true); setOpenPopupAction(1); setSelectedPromoData({}); }} /> */}
                        </div>

                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th className='tableHeaderStyle' onClick={() => handleSort('PromoCode')}>Promo Code</th>
                                <th className='tableHeaderStyle' onClick={() => handleSort('MaxTicket')}>Max Ticket</th>
                                <th className='tableHeaderStyle' onClick={() => handleSort('IsDefault')} style={{ textAlign: 'center', width: '150px' }}>Is Default ?</th>
                                <th className='tableHeaderStyle' onClick={() => handleSort('IsActive')} style={{ textAlign: 'center', width: '150px' }}>Is Active ?</th>
                                <th className='tableHeaderStyle' style={{ textAlign: 'center' }}><img src={filtericon} alt='filtericon' /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {!sortedPromos.length
                                ? <tr>
                                    <td colSpan={columnCount} style={{ textAlign: 'center', padding: '10px', fontSize: '16px' }}>
                                        No Record Found ☹️
                                    </td>
                                </tr>
                                : sortedPromos.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                                    .map((promoItem, index) => (
                                        <tr key={index} style={{ ...tableRowStyle, backgroundColor: index % 2 === 1 ? '#ececec' : '#FFFFFF' }}>
                                            <td className='tableCellStyle'>{promoItem.PromoCode}</td>
                                            <td className='tableCellStyle'>{promoItem.MaxTicket}</td>
                                            <td className="tableCellStyle" style={{ textAlign: 'center', width: '150px' }}>
                                                <span
                                                    style={{
                                                        backgroundColor: promoItem.IsDefault ? '#d1fae5' : '#fee2e2',
                                                        color: promoItem.IsDefault ? '#065f46' : '#991b1b',
                                                        padding: '4px 16px',
                                                        borderRadius: '16px',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                    }}
                                                >
                                                    {promoItem.IsDefault ? 'True' : 'False'}
                                                </span>
                                            </td>
                                            <td className="tableCellStyle" style={{ textAlign: 'center', width: '150px' }}>
                                                <span
                                                    style={{
                                                        backgroundColor: promoItem.IsActive ? '#d1fae5' : '#fee2e2',
                                                        color: promoItem.IsActive ? '#065f46' : '#991b1b',
                                                        padding: '4px 16px',
                                                        borderRadius: '16px',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                    }}
                                                >
                                                    {promoItem.IsActive ? 'True' : 'False'}
                                                </span>
                                            </td>
                                            <td className='tableCellStyle' style={{ textAlign: 'center', width: '100px' }}>
                                                {/* <img src={editsquare} alt='editsquare' style={{ cursor: 'pointer', paddingRight: '4px' }} onClick={() => { setOpenPopup(true); setOpenPopupAction(1); setSelectedPromoData(promoItem); }} /> */}
                                                {/* <Tooltip title="Ticket Rate" arrow>
                                                    <img src={ticketicon} alt='ticketicon' style={{ cursor: 'pointer' }} onClick={() => navigate('/event-ticketrate', { state: { EventMasterId: eventMasterId, TicketPromoCodeId: promoItem.TicketPromoCodeId } })} />
                                                </Tooltip> */}
                                                <Tooltip title="Delete Promocode" arrow>
                                                    <img src={deleteicon} alt='deleteicon' style={{ cursor: 'pointer', paddingLeft: '4px' }} onClick={() => { setOpenPopup(true); setOpenPopupAction(2); setSelectedPromoData(promoItem); }} />
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    ))}
                        </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '10px' }}>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100, 200, 500, 1000]}
                            component="div"
                            count={maxRows}
                            rowsPerPage={rowsPerPage}
                            page={currentPage - 1} // MUI TablePagination uses 0-based index
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            id="page-box"
                        />
                        <div style={{ fontSize: '13px', marginLeft: '10px' }}>
                            Page: <input style={{ width: '50px', border: '1px solid #ccc', borderRadius: '5px', padding: '4px' }} type="number" value={gotoPage} disabled={totalPages <= 1} onChange={handleGotoPageChange} onKeyDown={handleSubmitGotoPage} /> Of {totalPages}
                        </div>
                    </div>
                </div>

                {/* Popup */}
                {openPopup && openPopupAction === 1 &&
                    <AddUpdatePromocode
                        openPopup={openPopup}
                        loading={loading}
                        eventMasterId={eventMasterId}
                        selectedPromoData={selectedPromoData}
                        handleClose={handleClose} />
                }

                {openPopup && openPopupAction === 2 &&
                    <DialogComponent
                        isOpen={openPopup}
                        Title="Delete Promocode"
                        ActionText={"Delete"}
                        IsLoading={loading}
                        onSave={handleDeletePromoCode}
                        onClose={handleClose}
                    >
                        <label>Are you sure you want to delete this Promocode ?</label>
                    </DialogComponent>
                }
            </React.Fragment>
    );
};
// #endregion

const tableRowStyle = {
    borderBottom: '1px solid #eee',
};


export default PromoCode;